'use client';

import type { PostGridProps } from '@routeroyal/theme-core';
import { AllysPostCard } from './AllysPostCard';

/**
 * AllysPostGrid - Allys-themed post grid
 * Displays posts in a grid with the Allys design language
 */
export function AllysPostGrid({
  posts,
  variant = 'grid',
  columns = 3,
  cardVariant = 'default',
  showExcerpt = true,
  showCategory = true,
  showAuthor = true,
  emptyMessage,
  locale = 'en',
  basePath = '/blog',
  className = '',
  messages,
}: PostGridProps) {
  // Empty state
  if (!posts || posts.length === 0) {
    return (
      <div className={`text-center py-16 ${className}`}>
        <div className="max-w-md mx-auto">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#BBA339]/10 flex items-center justify-center">
            <svg className="w-8 h-8 text-[#BBA339]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-[#243462] mb-2 [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]">
            {emptyMessage || messages?.noPosts || 'No posts found'}
          </h3>
          {messages?.noPostsDescription && (
            <p className="text-gray-500 [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif]">
              {messages.noPostsDescription}
            </p>
          )}
        </div>
      </div>
    );
  }

  // List variant
  if (variant === 'list') {
    return (
      <div className={`flex flex-col gap-4 [font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif] ${className}`}>
        {posts.map((post) => (
          <AllysPostCard
            key={post.id}
            post={post}
            variant="horizontal"
            showExcerpt={showExcerpt}
            showCategory={showCategory}
            showAuthor={showAuthor}
            locale={locale}
            basePath={basePath}
            {...(messages && { messages })}
          />
        ))}
      </div>
    );
  }

  // Masonry variant
  if (variant === 'masonry') {
    return (
      <div
        className={`[font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif] ${className}`}
        style={{
          columnCount: columns,
          columnGap: '1.5rem',
        }}
      >
        {posts.map((post) => (
          <div key={post.id} className="break-inside-avoid mb-6">
            <AllysPostCard
              post={post}
              variant={cardVariant}
              showExcerpt={showExcerpt}
              showCategory={showCategory}
              showAuthor={showAuthor}
              locale={locale}
              basePath={basePath}
              {...(messages && { messages })}
            />
          </div>
        ))}
      </div>
    );
  }

  // Grid variant (default) - with featured first post
  const gridColsClass = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
  }[columns];

  // Featured first article
  const featuredPost = posts.length > 3 && columns >= 2 ? posts[0] : null;
  const regularPosts = featuredPost ? posts.slice(1) : posts;

  return (
    <div className={`[font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe_UI',system-ui,sans-serif] ${className}`}>
      {/* Section heading for accessibility - visually hidden */}
      <h2 className="sr-only">{(messages as Record<string, string> | undefined)?.postsSection || 'Latest Articles'}</h2>
      
      {/* Featured Post */}
      {featuredPost && (
        <div className="mb-8">
          <AllysPostCard
            post={featuredPost}
            variant="featured"
            showExcerpt={true}
            showCategory={showCategory}
            showAuthor={showAuthor}
            locale={locale}
            basePath={basePath}
            {...(messages && { messages })}
          />
        </div>
      )}

      {/* Regular Grid */}
      <div className={`grid ${gridColsClass} gap-6`}>
        {regularPosts.map((post) => (
          <AllysPostCard
            key={post.id}
            post={post}
            variant={cardVariant}
            showExcerpt={showExcerpt}
            showCategory={showCategory}
            showAuthor={showAuthor}
            locale={locale}
            basePath={basePath}
            {...(messages && { messages })}
          />
        ))}
      </div>
    </div>
  );
}
